import axios from 'axios'
import { parse } from 'query-string'
import { FC, useEffect, useState, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Spinner } from 'react-bootstrap-v5'
import { getIntegrationURLFromENV, handleSettingEnv } from '../../../utils/getIntegrationURLFromENV'
import {
  resetLoan,
  setCompanyVerificationMethod,
  setDecisionMaker,
  setDirector,
  setEntityData,
  setFundDetails,
  setGuarantor,
  setLoanApplication,
  setLoanEligibility,
  setLoanStep,
  setLoanSubStep,
  setPartnerDetails,
  setPersonData,
  setShareholders,
  setSingPassInfo,
} from './redux/actions'
import { LoanApplicationLocalStorageKeys } from '../../../utils/constants/LocalStorageKeys.enum'
import moment from 'moment'
import './AgentLoanType.scss'
import {
  Box,
  IconButton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tooltip,
} from '@mui/material'
import { useLazyQuery } from '@apollo/client'
import query from '../../../setup/graphql/query'
import NoDataProvided from './NoDataProvided'
import { RootState } from '../../../setup'
import { formatEntityDataForRedux } from '../../../constants/commonFunctions'
import EastIcon from '@mui/icons-material/East'
import RefreshIcon from '@mui/icons-material/Refresh'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import './AgentApplicationDashboard.scss'

const AgentApplicationDashboard: FC = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const searchQuery: any = parse(history.location.search)
  const agentCode = localStorage.getItem('agentCode')
  const responseUser: any = useSelector((state: RootState) => state.authUser.authUser)
  const [loading, setLoading] = useState<boolean>(false)
  const [downloadingRows, setDownloadingRows] = useState<Record<string, boolean>>({})

  let fullName
  if (responseUser) {
    fullName = responseUser.firstName + ' ' + responseUser.lastName
  } else {
    fullName =
      JSON.parse(localStorage.getItem('userDetails') || '')?.firstName +
      ' ' +
      JSON.parse(localStorage.getItem('userDetails') || '')?.lastName
  }

  const tabs = [
    {
      label: 'In Progress',
      value: 'approvalPending',
    },
    {
      label: 'Submitted',
      value: 'approved',
    },
    {
      label: 'All Applications',
      value: 'allApplication',
    },
  ]
  // Initialize with first tab's value
  const [selectedTabValue, setSelectedTabValue] = useState<string>(tabs[2].value)
  const [applications, setApplication] = useState([])
  const [value, setValue] = useState({
    allApplication: 0,
    approved: 0,
    approvalPending: 0,
  })
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [getloanApplications, onSuccessGetApplications] = useLazyQuery(query.GET_APPLICATIONS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    nextFetchPolicy: 'no-cache',
  })

  const [downloadPdfReport] = useLazyQuery(query.DOWNLOAD_APPLICATION_REPORT, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  })

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTabValue(newValue)
    setPage(0) // Reset to first page when changing tabs
  }

  // Filter applications based on selected tab
  const filteredApplications = useMemo(() => {
    // return []
    if (!applications?.length) return []

    const applicationCounts = {
      allApplication: applications?.length,
      approvalPending: applications.filter(
        (app: any) => app.applicationStatus === 'in-progress' || app.workflowStaus === 'in-prgoress'
      )?.length,
      approved: applications.filter(
        (app: any) => app.applicationStatus === 'submitted' || app.workflowStaus === 'submitted'
      )?.length,
    }

    setValue(applicationCounts)

    // Change default tab to allApplication if approvalPending is 0
    if (applicationCounts.approvalPending === 0 && selectedTabValue === 'approvalPending') {
      setSelectedTabValue('allApplication')
    }

    switch (selectedTabValue) {
      case 'approved':
        return applications.filter(
          (app: any) => app.applicationStatus === 'submitted' || app.workflowStaus === 'submitted'
        )
      case 'approvalPending':
        return applications.filter(
          (app: any) =>
            app.applicationStatus === 'in-progress' || app.workflowStaus === 'in-prgoress'
        )
      default: // 'allApplication'
        return applications
    }
  }, [applications, selectedTabValue, setValue])

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0) // Reset to the first page
  }

  const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage)
  }

  const setApplicationData = (loanApplication: any) => {
    if (loanApplication !== null) {
      const { loanEligibility, shareholderKycStatus, externalDataStore } = loanApplication
      const singpassInfo = externalDataStore.filter((o: any) => o.type === 'singpass')

      if (loanEligibility !== null) {
        dispatch(setLoanEligibility(loanEligibility))
      } else if (loanEligibility === null) {
        dispatch(setLoanEligibility(null))
      }

      if (shareholderKycStatus.length > 0) {
        const decisionMaker = shareholderKycStatus.filter((o: any) => o.isDecisionMaker)[0]
        const gurantors = shareholderKycStatus.filter((o: any) => o.isGuarantor)
        const directors = shareholderKycStatus.filter((o: any) => o.isDirector)

        dispatch(setGuarantor(gurantors))
        dispatch(setDirector(directors))
        dispatch(setDecisionMaker(decisionMaker ? decisionMaker : null))
      } else if (shareholderKycStatus.length === 0) {
        dispatch(setGuarantor([]))
        dispatch(setDirector([]))
        dispatch(setDecisionMaker(null))
      }

      if (singpassInfo.length > 0) {
        dispatch(setSingPassInfo(singpassInfo[0].data))

        dispatch(setEntityData(formatEntityDataForRedux(singpassInfo[0].data)))
      } else if (singpassInfo.length === 0) {
        dispatch(setSingPassInfo(null))
        dispatch(setEntityData(null))
      }

      dispatch(setLoanApplication(loanApplication))
    }
  }

  useEffect(() => {
    if (onSuccessGetApplications.error) {
      // Handle error state
      setLoading(false)
    }
  }, [onSuccessGetApplications.error])

  // Function to fetch loan applications that can be called anytime
  const fetchLoanApplications = () => {
    if (agentCode) {
      setLoading(true)
      // Clear existing data immediately for a more responsive feel
      setApplication([])
      setDownloadingRows({})

      getloanApplications({
        variables: {
          agentCode: agentCode,
          appId: null,
        },
        onCompleted: (data) => {
          // Process data in the callback for faster display
          if (data && data.getLatestApplications) {
            setApplication(data.getLatestApplications)
          }
          setLoading(false)
        },
        onError: (error) => {
          console.error('Error fetching applications:', error)
          setLoading(false)
        },
      })
    }
  }

  // Initial setup and data loading
  useEffect(() => {
    handleSettingEnv(searchQuery?.env)
    localStorage.removeItem(LoanApplicationLocalStorageKeys.LOAN_APPLICATION_SUBMISSION_METHOD)

    /**
     * Setting Partner Id
     */
    if (searchQuery?.partner && !localStorage.getItem('partnerId')) {
      getPartnerDetails(searchQuery?.partner || localStorage.getItem('partnerId'))
    }

    fetchLoanApplications()
    // eslint-disable-next-line
  }, [])

  const getPartnerDetails = async (partnerId: string) => {
    const env = localStorage.getItem('env')
    const API_URL = getIntegrationURLFromENV(searchQuery?.env || env)

    const partnerData = await axios.get(`${API_URL}ext/api/businesspartner/${partnerId || ''}`)

    if (partnerData.data && partnerData.status === 200) {
      localStorage.setItem('partnerId', partnerData.data?.id)
      localStorage.setItem('partnerDetails', JSON.stringify(partnerData.data))
      dispatch(setPartnerDetails(partnerData.data))
    }
  }

  const downloadPDF = useCallback(
    async (applicationId: string) => {
      try {
        setDownloadingRows((prev) => ({
          ...prev,
          [applicationId]: true,
        }))
        const { data } = await downloadPdfReport({
          variables: { applicationId },
        })

        setDownloadingRows((prev) => {
          const newState = { ...prev }
          delete newState[applicationId]
          return newState
        })

        if (data?.downloadApplicationReport) {
          const byteCharacters = atob(data.downloadApplicationReport)
          const byteNumbers = new Array(byteCharacters.length)
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }
          const byteArray = new Uint8Array(byteNumbers)
          const blob = new Blob([byteArray], { type: 'application/pdf' })
          const url = window.URL.createObjectURL(blob)

          const a = document.createElement('a')
          a.href = url
          a.download = `Report_${applicationId}_${Date.now()}.pdf`
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          window.URL.revokeObjectURL(url)
        }
      } catch (error) {
        console.error('Error downloading PDF:', error)
        throw error
      }
    },
    [downloadPdfReport]
  )

  const startFreshApplication = () => {
    window.open('/agent-application', '_blank')
    localStorage.removeItem('loanApplicationId')
    localStorage.removeItem('companyUEN')
    localStorage.removeItem('companyVerificationMethod')
    localStorage.removeItem('singpass')
    localStorage.removeItem('errorLogged')
    localStorage.removeItem('perfiosId')
    localStorage.removeItem('coApplicationId')
    localStorage.removeItem('accountDetails')
    localStorage.removeItem('uploadedFiles')
    localStorage.removeItem('loanType')
    localStorage.removeItem('partnerApplicationType')
    localStorage.removeItem(LoanApplicationLocalStorageKeys.LOAN_APPLICATION_SUBMISSION_METHOD)
    // Reset all Redux state to initial values
    dispatch(resetLoan())
    dispatch(setLoanApplication(null))
    dispatch(setLoanEligibility(null))
    dispatch(setEntityData(null))
    dispatch(setPersonData(null))
    dispatch(setShareholders([]))
    dispatch(setGuarantor([]))
    dispatch(setDirector([]))
    dispatch(setDecisionMaker(null))
    dispatch(setSingPassInfo(null))
    dispatch(setPartnerDetails(null))
    dispatch(setFundDetails(null))
    dispatch(setCompanyVerificationMethod(0))
    dispatch(setLoanStep(1))
    dispatch(setLoanSubStep(0))
  }

  return (
    <div className='d-flex flex-column flex-column-fluid loanApplication mx-lg-12 px-lg-12 mx-md-8 px-md-8 mx-sm-6 px-sm-6 mx-4 px-4'>
      <h1 className='mb-7 mt-5'>Welcome {fullName} ,</h1>
      <div className='cardNew'>
        <div className='section-heading'>
          <div className='d-flex flex-column flex-sm-row justify-content-sm-between align-items-sm-start gap-6'>
            <h2>Application Listings</h2>
            <Box sx={{ display: 'flex', gap: '16px' }}>
              {loading ? (
                <div
                  style={{
                    borderRadius: '50%',
                    width: '44px',
                    height: '44px',
                    border: '1px solid #ccc',
                    alignItems: 'center',
                    justifyContent: 'center',
                    display: 'flex',
                  }}
                >
                  <Spinner
                    style={{
                      height: '16px',
                      width: '16px',
                      borderLeftColor: '#ccc',
                      borderTopColor: '#ccc',
                      borderBottomColor: '#ccc',
                      borderRightColor: 'transparent',
                    }}
                    size={'sm'}
                    animation='border'
                  />
                </div>
              ) : (
                <button
                  className='button button-outline-primary-template continue-btn'
                  style={{
                    borderRadius: '50%',
                    width: '44px',
                    height: '44px',
                  }}
                  onClick={() => fetchLoanApplications()}
                >
                  <Tooltip title='Refresh Applications'>
                    <RefreshIcon sx={{ color: 'var(--primary-color)' }} />
                  </Tooltip>
                </button>
              )}
              <button
                className='button button-primary start-app-btn text-nowrap'
                onClick={() => {
                  startFreshApplication()
                }}
              >
                Start New Application
              </button>
            </Box>
          </div>
          <hr className='text-gray-400' />
          <Box display='flex' flexDirection='column' maxWidth='100%' width='100%'>
            <div
              className='d-flex align-items-center justify-content-between'
              style={{ borderBottom: '1px solid #F3F5F5', marginBottom: '1.5rem', width: '100%' }}
            >
              <div style={{ flexGrow: 1, width: '100%' }}>
                <Tabs
                  value={selectedTabValue}
                  onChange={handleTabChange}
                  variant='scrollable'
                  scrollButtons='auto'
                  TabIndicatorProps={{ style: { height: '1px' } }}
                  sx={{
                    '& .MuiTabs-flexContainer': {
                      // flexWrap: 'wrap',
                    },
                    '& .MuiTab-root.Mui-selected': {
                      color: 'var(--secondary-color) !important',
                    },
                    '& .MuiTabs-indicator': {
                      zIndex: 9,
                      background: 'var(--secondary-color)',
                    },
                    '& .MuiTab-root.Mui-selected .numbers': {
                      backgroundColor: 'var(--secondary-light-color) !important',
                    },
                  }}
                >
                  {tabs?.map((tab) => (
                    <Tab
                      key={tab.value}
                      value={tab.value}
                      sx={{
                        textTransform: 'none',
                        opacity: 1,
                        paddingLeft: 5,
                        paddingRight: 5,
                        fontSize: 14,
                        gap: 4,
                      }}
                      disabled={value[tab.value as keyof typeof value] === 0}
                      label={
                        <>
                          <span style={{ display: 'flex', alignItems: 'center' }}>
                            {tab.label}
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '32px',
                                height: '25px',
                                opacity: 1,
                                borderRadius: '12px',
                                backgroundColor: '#f8f8f8',
                                fontSize: '12px',
                                marginLeft: '8px',
                                lineHeight: '1',
                              }}
                            >
                              {value[tab.value as keyof typeof value]}
                            </span>
                          </span>
                        </>
                      }
                    />
                  ))}
                </Tabs>
              </div>
            </div>
          </Box>

          <div className='table-wrapper px-2'>
            <TableContainer>
              <Table
                sx={{
                  border: '1px solid #e8eaeb !important',
                  '.col1': { maxWidth: '88px' },
                  '.col6': { whiteSpace: 'normal' },
                  '.col7': { whiteSpace: 'normal' },
                  '.col8': { whiteSpace: 'normal' },
                  '.col9': { width: '50px' },
                }}
              >
                <TableHead className='table-application-dashboard'>
                  <TableRow>
                    <TableCell>Application Number</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Company Name</TableCell>
                    <TableCell>Company UEN</TableCell>
                    <TableCell>Director Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Myinfo Business</TableCell>
                    <TableCell>Singpass</TableCell>
                    <TableCell>Application</TableCell>
                    <TableCell>Approval</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody className='table-body-application'>
                  {loading ? (
                    // Generate multiple placeholder rows for better visual feedback
                    Array(5)
                      .fill(0)
                      .map((_, index) => (
                        <TableRow key={`skeleton-${index}`} sx={{ maxHeight: '70px' }}>
                          {/* Application Number */}
                          <TableCell>
                            <div
                              style={{
                                background: '#f3f3f3',
                                height: '20px',
                                width: '120px',
                                borderRadius: '4px',
                              }}
                            />
                          </TableCell>
                          {/* Date column */}
                          <TableCell sx={{ width: '88px' }}>
                            <div
                              style={{
                                background: '#f3f3f3',
                                height: '20px',
                                width: '70px',
                                borderRadius: '4px',
                              }}
                            />
                          </TableCell>
                          {/* Company name */}
                          <TableCell>
                            <div
                              style={{
                                background: '#f3f3f3',
                                height: '20px',
                                width: '120px',
                                borderRadius: '4px',
                              }}
                            />
                          </TableCell>
                          {/* UEN */}
                          <TableCell>
                            <div
                              style={{
                                background: '#f3f3f3',
                                height: '20px',
                                width: '100px',
                                borderRadius: '4px',
                              }}
                            />
                          </TableCell>
                          {/* Name */}
                          <TableCell>
                            <div
                              style={{
                                background: '#f3f3f3',
                                height: '20px',
                                width: '130px',
                                borderRadius: '4px',
                              }}
                            />
                          </TableCell>
                          {/* Email */}
                          <TableCell>
                            <div
                              style={{
                                background: '#f3f3f3',
                                height: '20px',
                                width: '140px',
                                borderRadius: '4px',
                              }}
                            />
                          </TableCell>
                          {/* Status columns */}
                          {Array(4)
                            .fill(0)
                            .map((_, statusIndex) => (
                              <TableCell key={`status-${statusIndex}`}>
                                <div
                                  style={{
                                    background: '#f3f3f3',
                                    height: '24px',
                                    width: '90px',
                                    borderRadius: '12px',
                                  }}
                                />
                              </TableCell>
                            ))}
                          {/* Action column */}
                          <TableCell>
                            <div
                              style={{
                                background: '#f3f3f3',
                                height: '24px',
                                width: '24px',
                                borderRadius: '12px',
                                margin: '0 auto',
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                  ) : filteredApplications?.length > 0 ? (
                    filteredApplications
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((application: any, index: number) => (
                        <TableRow key={application?.id} sx={{ height: '70px' }}>
                          <TableCell>
                            <span
                              style={{
                                whiteSpace: 'nowrap',
                                color: '#2e3192',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                              }}
                              onClick={() => {
                                localStorage.removeItem('companyVerificationMethod')
                                setApplicationData(application)

                                localStorage.setItem('loanApplicationId', application.id)

                                dispatch(
                                  setLoanStep(
                                    application?.applicationCompleteStep === 4
                                      ? 5
                                      : application?.applicationCompleteStep === 1.1
                                      ? 2
                                      : application?.applicationCompleteStep
                                  )
                                )
                                dispatch(
                                  setLoanSubStep(
                                    application?.applicationCompleteStep === 1.1 ||
                                      application?.applicationCompleteStep === 1.2 ||
                                      application?.applicationCompleteStep === 1.3
                                      ? 1
                                      : 0
                                  )
                                )
                                window.open(
                                  `/agent-application/${application?.id}?step=${
                                    application?.applicationCompleteStep === 4
                                      ? 5
                                      : application?.applicationCompleteStep === 1.1
                                      ? 2
                                      : application?.applicationCompleteStep
                                  }`,
                                  '_blank'
                                )
                              }}
                            >
                              {application?.applicationNumber || 'NA'}
                            </span>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', color: '#191919' }}>
                            {moment(application?.createdAt).format('DD MMM YYYY')}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', color: '#191919' }}>
                            <Tooltip title={application?.mibCompanyName || 'NA'}>
                              {application?.mibCompanyName?.length > 25
                                ? application?.mibCompanyName?.slice(0, 25) + '...'
                                : application?.mibCompanyName || 'NA'}
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', color: '#191919' }}>
                            {application?.mibCompanyUEN || 'NA'}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', color: '#191919' }}>
                            <Tooltip title={application?.applicantName || 'NA'}>
                              {application?.applicantName?.length > 25
                                ? application?.applicantName?.slice(0, 25) + '...'
                                : application?.applicantName || 'NA'}
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', color: '#191919' }}>
                            <Tooltip title={application?.businessOwnerEmail || 'NA'}>
                              {application?.businessOwnerEmail?.length > 25
                                ? application?.businessOwnerEmail?.slice(0, 25) + '...'
                                : application?.businessOwnerEmail || 'NA'}
                            </Tooltip>
                          </TableCell>
                          <TableCell
                            sx={{ whiteSpace: 'nowrap', width: '150px', color: '#191919' }}
                          >
                            {application?.dataViaSingpass ||
                            application?.applicationCompleteStep === 3 ? (
                              <span className='status statusWithBg submitted'>Submitted</span>
                            ) : application?.agentKybMail?.businessEmailAddress ? (
                              <span className='status statusWithBg in-progress'>In Progress</span>
                            ) : (
                              <span className='status statusWithBg in-progress'>Pending</span>
                            )}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {(() => {
                              if (!Array.isArray(application?.shareholderKycStatus)) {
                                return (
                                  <span className='status statusWithBg not-verified'>Pending</span>
                                )
                              }

                              const directors = application?.shareholderKycStatus.filter(
                                (shareholder: any) => shareholder?.isDirector
                              )

                              const verifiedDirectors = directors.filter(
                                (director: any) =>
                                  director?.dataViaSingpass || director?.kycStatus === 'completed'
                              )

                              // All directors are verified
                              if (
                                directors.length > 0 &&
                                directors.length === verifiedDirectors.length
                              ) {
                                return (
                                  <span className='status statusWithBg submitted'>Submitted</span>
                                )
                              }
                              // Some but not all directors are verified
                              else if (verifiedDirectors.length > 0) {
                                return (
                                  <span className='status statusWithBg in-progress'>
                                    In Progress
                                  </span>
                                )
                              }
                              // No directors are verified
                              else {
                                return (
                                  <span className='status statusWithBg not-verified'>Pending</span>
                                )
                              }
                            })()}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {application?.applicationStatus === 'in-progress' ? (
                              <span className='status statusWithBg in-progress'>In Progress</span>
                            ) : (
                              <span className='status statusWithBg submitted'>Submitted</span>
                            )}
                          </TableCell>

                          <TableCell>
                            {application?.workflowStatus === 'application-accepted' ? (
                              <span className='status statusWithBg submitted'>Approved</span>
                            ) : application?.workflowStatus === 'application-rejected' ||
                              application?.workflowStatus === 'bre-rejected' ? (
                              <span className='status statusWithBg rejected'>Rejected</span>
                            ) : application?.workflowStatus === 'active' ? (
                              <span className='status statusWithBg submitted'>Disbursed</span>
                            ) : (
                              <span>NA</span>
                            )}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
                            {downloadingRows[application.id] ? (
                              <Spinner
                                style={{
                                  height: '16px',
                                  width: '16px',
                                  borderLeftColor: '#000054',
                                  borderTopColor: '#000054',
                                  borderBottomColor: '#000054',
                                  borderRightColor: 'transparent',
                                }}
                                size={'sm'}
                                animation='border'
                              />
                            ) : application?.applicationStatus !== 'in-progress' ? (
                              <Tooltip title='Download Application'>
                                <IconButton>
                                  <FileDownloadOutlinedIcon
                                    sx={{ color: 'rgba(0, 0, 0, 0.87)' }}
                                    onClick={() => downloadPDF(application.id)}
                                  />
                                </IconButton>
                              </Tooltip>
                            ) : application?.applicationStatus === 'in-progress' ? (
                              <Tooltip title='Resume Application'>
                                <IconButton>
                                  <EastIcon
                                    sx={{ color: 'rgba(0, 0, 0, 0.87)' }}
                                    onClick={() => {
                                      localStorage.removeItem('companyVerificationMethod')
                                      setApplicationData(application)

                                      localStorage.setItem('loanApplicationId', application.id)

                                      dispatch(
                                        setLoanStep(
                                          application?.applicationCompleteStep === 4
                                            ? 5
                                            : application?.applicationCompleteStep === 1.1
                                            ? 2
                                            : application?.applicationCompleteStep
                                        )
                                      )
                                      dispatch(
                                        setLoanSubStep(
                                          application?.applicationCompleteStep === 1.1 ||
                                            application?.applicationCompleteStep === 1.2 ||
                                            application?.applicationCompleteStep === 1.3
                                            ? 1
                                            : 0
                                        )
                                      )
                                      window.open(
                                        `/agent-application/${application?.id}?step=${
                                          application?.applicationCompleteStep === 4
                                            ? 5
                                            : application?.applicationCompleteStep === 1.1
                                            ? 2
                                            : application?.applicationCompleteStep
                                        }`,
                                        '_blank'
                                      )
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow sx={{ height: '250px' }}>
                      <TableCell colSpan={12} sx={{ '&.MuiTableCell-root': { border: 'none' } }}>
                        <NoDataProvided />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {/* Pagination */}
            {filteredApplications?.length > 0 && (
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component='div'
                count={filteredApplications?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handlePageChange}
                labelRowsPerPage={'Rows Per Page'}
                labelDisplayedRows={({ from, to, count }) =>
                  `Showing ${from}-${to} of ${count !== -1 ? count : `${'more than'} ${to}`}`
                }
                onRowsPerPageChange={handlePageSizeChange}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AgentApplicationDashboard
